import { z } from 'zod'
import { RegistrationSchema } from './booking'
import type { AogBlock } from './defects'

// A row of GET /api/aircraft-maintenance-events. An aircraft is "in
// maintenance" while it has an OPEN event (closedAt null): there is no flag
// on the aircraft. startsOn and endsOn are club dates, both inclusive.
export interface AircraftMaintenanceEvent {
  eventId: number
  registration: string
  startsOn: string
  endsOn: string
  note: string
  createdBy: string | null
  createdByName: string | null
  createdAt: string
  closedAt: string | null
}

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const dateRange = (v: { startsOn?: string; endsOn?: string }) => !v.startsOn || !v.endsOn || v.endsOn >= v.startsOn

export const CreateMaintenanceEventSchema = z
  .object({
    registration: RegistrationSchema,
    startsOn: IsoDate,
    endsOn: IsoDate,
    note: z.string().trim().max(1000).default(''),
  })
  .refine(dateRange, { message: 'endsOn must not be before startsOn', path: ['endsOn'] })

export const UpdateMaintenanceEventSchema = z
  .object({
    startsOn: IsoDate,
    endsOn: IsoDate,
    note: z.string().trim().max(1000),
    // true closes the event (the aircraft is back); false reopens it.
    closed: z.boolean(),
  })
  .partial()
  .refine(dateRange, { message: 'endsOn must not be before startsOn', path: ['endsOn'] })

type EventWindow = Pick<AircraftMaintenanceEvent, 'startsOn' | 'endsOn' | 'closedAt'>

// The calendar block an aircraft's open events make, in the shape of the AOG
// block so the booking rule is the same one. `until` is null once the
// estimated range has lapsed with the event still open: the block stays
// until someone closes or extends the event.
export function maintenanceBlock(events: ReadonlyArray<EventWindow>, today: string): AogBlock | null {
  const open = events.filter((e) => e.closedAt === null)
  if (open.length === 0) return null
  const from = open.map((e) => e.startsOn).sort()[0] as string
  const until = open.map((e) => e.endsOn).sort().at(-1) as string
  const elapsed = until < today
  return { from, until: elapsed ? null : until, elapsed }
}

// Is the aircraft in maintenance today? An event entered ahead of time
// blocks the calendar from its first day but the aircraft is not grey yet.
export const inMaintenance = (events: ReadonlyArray<EventWindow>, today: string): boolean =>
  events.some((e) => e.closedAt === null && e.startsOn <= today)
